/* hero-custom.js — carousel + audio + image→video crossfade */
(function () {
  'use strict';

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  class HeroCustom {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('.hero-custom__slide'));
      this.dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
      this.prevBtn = root.querySelector('[data-hero-prev]');
      this.nextBtn = root.querySelector('[data-hero-next]');
      this.muteBtn = root.querySelector('[data-hero-mute]');
      this.audio = root.querySelector('[data-hero-audio]');
      this.progressBar = root.querySelector('[data-hero-progress-bar]');

      this.current = 0;
      this.timer = null;
      this.progressInterval = null;
      this.isPaused = false;

      this.autoplay = root.dataset.autoplay === 'true';
      this.interval = parseInt(root.dataset.interval, 10) || 6000;
      this.mobileInterval = parseInt(root.dataset.mobileInterval, 10) || 5000;
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.loop = root.dataset.loop === 'true';
      this.allowSwipe = root.dataset.allowSwipe === 'true';

      this.bindEvents();
      this.setupVideoCrossfade();
      this.setupBreakpointVideos();
      this.measureTopOffset();
      this.start();
    }

    measureTopOffset() {
      const apply = () => {
        this.root.style.setProperty('--hero-top-offset', '0px');
        const top = Math.max(0, this.root.getBoundingClientRect().top + window.scrollY);
        this.root.style.setProperty('--hero-top-offset', top + 'px');

        // How many px the site header covers the hero's top edge. For an
        // overlay (transparent) header this ~= header height; for a header in
        // normal flow above the hero it ~= 0. Cap at the header's own height so
        // the value stays stable when a fixed/sticky header is scrolled past.
        // Prefer the concrete header element (its offsetHeight is reliable even
        // when it's fixed/sticky and the section-group wrapper collapses to 0).
        const hdr = document.querySelector('bb-header')
          || document.querySelector('header-dual, .hdl')
          || document.querySelector('.shopify-section-group-header-group')
          || document.querySelector('.section-header');
        let overlap = 0;
        if (hdr) {
          const hh = hdr.offsetHeight || 0;
          const gap = hdr.getBoundingClientRect().bottom - this.root.getBoundingClientRect().top;
          overlap = Math.min(hh, Math.max(0, gap));
        }
        this.root.style.setProperty('--hero-header-overlap', overlap + 'px');
      };
      apply();
      window.addEventListener('resize', apply);
      window.addEventListener('load', apply);

      // Re-measure whenever the header (or anything above the hero) changes size.
      // Watches the header section group and the topbar specifically.
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => requestAnimationFrame(apply));
        document.querySelectorAll('bb-header, .topbar, .shopify-section-group-header-group').forEach(el => ro.observe(el));
      }

      // Theme editor: re-fire when any section reloads (header height slider, etc.)
      document.addEventListener('shopify:section:load',   apply);
      document.addEventListener('shopify:section:unload', apply);
    }

    getInterval(slide) {
      const overrideMs = parseInt(slide?.dataset.slideDuration, 10);
      if (overrideMs && overrideMs > 0) return overrideMs;
      return isMobile() ? this.mobileInterval : this.interval;
    }

    bindEvents() {
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next(true));
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev(true));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.heroDot, 10);
          this.goTo(idx, true);
        });
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => this.pause());
        this.root.addEventListener('mouseleave', () => this.resume());
      }

      if (this.muteBtn && this.audio) {
        this.muteBtn.addEventListener('click', () => this.toggleAudio());
      }

      if (this.allowSwipe) this.setupSwipe();

      // Pause when tab hidden
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause(); else this.resume();
      });
    }

    setupSwipe() {
      let startX = 0;
      let startY = 0;
      let active = false;
      this.root.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        active = true;
      }, { passive: true });
      this.root.addEventListener('touchend', (e) => {
        if (!active) return;
        active = false;
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const dx = endX - startX;
        const dy = endY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.next(true); else this.prev(true);
        }
      }, { passive: true });
    }

    setupVideoCrossfade() {
      this.slides.forEach((slide) => {
        const mode = slide.dataset.backgroundMode;
        if (mode === 'image') return;
        const videos = slide.querySelectorAll('[data-hero-video]');
        videos.forEach((video) => {
          video.addEventListener('canplay', () => {
            if (mode === 'image-to-video' || mode === 'video') {
              video.classList.add('is-playing');
            }
          });
        });
      });
    }

    /* Each slide ships a <video> per breakpoint and CSS hides one with display:none.
       That does NOT stop Safari autoplaying and downloading the hidden one — measured
       at 62MB of the invisible desktop video on an iPhone, which starved the visible
       one. So sources ship as data-src (see snippets/hero-video-sources) and only the
       video that is genuinely on screen ever gets wired up. */
    setupBreakpointVideos() {
      const apply = () => {
        this.slides.forEach((slide) => {
          if (slide.dataset.backgroundMode === 'image') return;
          const videos = Array.from(slide.querySelectorAll('video[data-hero-video]'));
          if (!videos.length) return;
          // Ask the CSS which one is showing rather than re-deriving the breakpoint,
          // so this can never disagree with the stylesheet.
          const visible = videos.find((v) => getComputedStyle(v).display !== 'none');
          videos.forEach((v) => (v === visible ? this.attachVideo(v) : this.detachVideo(v)));
        });
      };
      apply();

      let debounce = null;
      window.addEventListener('resize', () => {
        clearTimeout(debounce);
        debounce = setTimeout(apply, 200);
      });
    }

    attachVideo(video) {
      if (video.dataset.heroAttached === 'true') return;
      const sources = video.querySelectorAll('source[data-src]');
      if (!sources.length) return;
      sources.forEach((s) => s.setAttribute('src', s.dataset.src));
      video.dataset.heroAttached = 'true';
      video.preload = 'auto';
      video.load();
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }

    detachVideo(video) {
      if (video.dataset.heroAttached !== 'true') return; // never loaded — nothing to undo
      video.pause();
      video.querySelectorAll('source[src]').forEach((s) => s.removeAttribute('src'));
      video.dataset.heroAttached = 'false';
      video.load(); // releases the buffer and the decoder
    }

    // The visible video for a slide — not just the first, which is the desktop one
    // and is deliberately left unloaded on mobile.
    visibleVideo(slide) {
      const videos = Array.from(slide.querySelectorAll('video[data-hero-video]'));
      return videos.find((v) => getComputedStyle(v).display !== 'none') || videos[0] || null;
    }

    goTo(index, manual) {
      if (index === this.current) return;
      const prev = this.slides[this.current];
      const next = this.slides[index];
      if (!next) return;
      this.slides.forEach((s) => s.classList.remove('is-active', 'is-prev'));
      if (prev) prev.classList.add('is-prev');
      next.classList.add('is-active');

      // Restart video on the now-active slide if any
      const v = this.visibleVideo(next);
      if (v && (next.dataset.backgroundMode === 'video' || next.dataset.backgroundMode === 'image-to-video')) {
        try { v.currentTime = 0; v.play(); } catch (e) {}
      }

      this.dots.forEach((d) => d.classList.remove('is-active'));
      if (this.dots[index]) this.dots[index].classList.add('is-active');

      this.current = index;
      if (manual) this.restart();
    }

    next(manual) {
      let n = this.current + 1;
      if (n >= this.slides.length) {
        if (this.loop) n = 0; else return;
      }
      this.goTo(n, manual);
    }

    prev(manual) {
      let n = this.current - 1;
      if (n < 0) {
        if (this.loop) n = this.slides.length - 1; else return;
      }
      this.goTo(n, manual);
    }

    start() {
      if (!this.autoplay || this.slides.length <= 1) return;
      this.scheduleNext();
    }

    scheduleNext() {
      this.clearTimers();
      const duration = this.getInterval(this.slides[this.current]);
      this.timer = setTimeout(() => this.next(false), duration);

      if (this.progressBar) {
        let elapsed = 0;
        const tick = 50;
        this.progressInterval = setInterval(() => {
          if (this.isPaused) return;
          elapsed += tick;
          this.progressBar.style.width = Math.min(100, (elapsed / duration) * 100) + '%';
        }, tick);
        this.progressBar.style.width = '0%';
      }
    }

    clearTimers() {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
      if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
    }

    pause() {
      this.isPaused = true;
      this.clearTimers();
    }

    resume() {
      if (!this.autoplay || this.slides.length <= 1) return;
      this.isPaused = false;
      this.scheduleNext();
    }

    restart() {
      if (!this.autoplay || this.slides.length <= 1) return;
      this.scheduleNext();
    }

    toggleAudio() {
      if (!this.audio) return;
      const muted = this.muteBtn.getAttribute('aria-pressed') !== 'true';
      // aria-pressed=true means "currently unmuted" (button shows the unmuted icon)
      if (muted) {
        this.audio.muted = false;
        this.audio.play().catch(() => {});
        this.muteBtn.setAttribute('aria-pressed', 'true');
      } else {
        this.audio.muted = true;
        this.muteBtn.setAttribute('aria-pressed', 'false');
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-hero-custom]').forEach((el) => {
      if (el.__heroInit) return;
      el.__heroInit = true;
      new HeroCustom(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify section is reloaded in theme editor
  document.addEventListener('shopify:section:load', init);
})();
